import { DatePickerInput } from "@mantine/dates";
import { IconCalendar } from "@tabler/icons-react";
import { useTranslation } from 'react-i18next';
import dayjs from "dayjs";
import { useState, useEffect } from 'react';
import { Loader } from "@mantine/core";

type Props = {
    value: [string, string] | string,
    label?: string,
    min_max?: [string, string], // min and max date
    onChange: (value: [string, string]) => void, // onChange will always return a range [date, null]
    className?: string,
    clearable?: boolean,
    hideLabel?: boolean,
    variant?: 'default' | 'filled' | 'unstyled' | 'outline' | 'default',
    size?: 'sm' | 'md' | 'lg' | 'xs' | 'xl',
    placeholder?: string,
    valueFormat?: string,
    hideIcon?: boolean,
    loading?: boolean,
    disabled?: boolean,
}

export default function DatePickerCustom({
    value,
    label,
    min_max,
    onChange,
    className,
    clearable = true,
    hideLabel = false,
    variant = 'default',
    size = 'sm',
    placeholder,
    valueFormat = 'DD MMMM, YYYY',
    hideIcon = false,
    loading = false,
    disabled = false,
}: Props) {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const baseClassName = 'whitespace-nowrap';

    const handleChange = (val: any) => {
        if (Array.isArray(val)) {
            const d1 = val[0] ? dayjs(val[0]).format('YYYY-MM-DD') : '';
            const d2 = val[1] ? dayjs(val[1]).format('YYYY-MM-DD') : '';
            const newRange: [string, string] = [d1, d2];
            setLocalValue(newRange);
            onChange(newRange);
        } else {
            const d = val ? dayjs(val).format('YYYY-MM-DD') : '';
            setLocalValue(d);
            onChange([d, '']);
        }
    }

    const handleClose = () => {
        const hasOneSelected = Array.isArray(localValue) && localValue[0] && !localValue[1];
        if (hasOneSelected) {
            setLocalValue(value);
        }
    };

    return (
        <div className={className}>
            {
                Array.isArray(value)
                    ? (
                        <DatePickerInput
                            highlightToday={true}
                            placeholder={placeholder}
                            size={size}
                            numberOfColumns={2}
                            variant={variant}
                            radius="md"
                            className={`${baseClassName} min-w-[250px]`}
                            label={hideLabel ? '' : label || t('dates')}
                            type="range"
                            allowSingleDateInRange
                            disabled={disabled}
                            value={
                                Array.isArray(localValue) && localValue[0]
                                    ? [
                                        localValue[0] ? dayjs(localValue[0]).toDate() : null,
                                        localValue[1] ? dayjs(localValue[1]).toDate() : null
                                    ]
                                    : [null, null]
                            }
                            onChange={handleChange}
                            valueFormat={valueFormat}
                            locale={currentLang}
                            clearable={clearable}
                            minDate={min_max?.[0]}
                            maxDate={min_max?.[1]}
                            leftSection={hideIcon ? null : (
                                loading ? <Loader size="xs" color="black" /> : <IconCalendar size={16} stroke={1.5} />
                            )}
                            leftSectionPointerEvents="none"
                            popoverProps={{
                                withinPortal: true,
                                closeOnClickOutside: true,
                                closeOnEscape: true,
                                keepMounted: false,
                                onClose: handleClose
                            }}
                        />
                    )
                    : (
                        <DatePickerInput
                            highlightToday={true}
                            placeholder={placeholder}
                            variant={variant}
                            size={size}
                            radius="md"
                            className={`${baseClassName} min-w-[160px]`}
                            label={hideLabel ? '' : label || t('date')}
                            value={localValue ? dayjs(localValue as string).toDate() : null}
                            disabled={disabled}
                            onChange={handleChange}
                            valueFormat={valueFormat}
                            locale={currentLang}
                            clearable={clearable}
                            leftSectionPointerEvents="none"
                            leftSection={hideIcon ? null : (
                                loading ? <Loader size="xs" color="black" /> : <IconCalendar size={16} stroke={1.5} />
                            )}
                            minDate={min_max?.[0]}
                            maxDate={min_max?.[1]}
                            popoverProps={{
                                withinPortal: true,
                                closeOnClickOutside: true,
                                closeOnEscape: true,
                                keepMounted: false,
                                onClose: handleClose
                            }}
                        />
                    )
            }
        </div>
    );
}